import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "../../assets/images/logo/grey_compress.png";

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="policy-page-wrap">
      <Helmet>
        <title>Privacy Policy | 3D Spot</title>
        <meta
          name="description"
          content="3D Spot Privacy Policy covering GDPR, ePrivacy, CalOPPA, Meta Pixel, Meta Lead Forms, advertising cookies, user rights, and data protection."
        />
      </Helmet>

      <div className="policy-topbar">
        <Link to="/" className="policy-brand">
          <img src={logo} alt="3D Spot Logo" />
          <span>3D Spot</span>
        </Link>

        <div className="policy-actions">
          <button type="button" onClick={() => navigate(-1)}>
            Back
          </button>

          <Link to="/" className="policy-home-btn">
            Home
          </Link>
        </div>
      </div>

      <div className="privacy-policy">
        <h1>Privacy Policy</h1>

        <p className="text-center mb-8">
          <strong>Last updated:</strong> June 29, 2026
        </p>

        <p>
          This Privacy Policy explains how <strong>3D Spot</strong> (“Company,”
          “we,” “us,” or “our”) collects, uses, stores, shares, and protects
          personal information when you visit our website, contact us, interact
          with our Facebook or Instagram pages, submit a Meta Lead Form, request
          a quotation, place an order, or use our custom 3D sculpting/STL
          services.
        </p>

        <p>
          By using our website or contacting us, you agree to this Privacy
          Policy.
        </p>

        <h2>1. Who We Are</h2>

        <p>
          3D Spot provides custom 3D sculpting, digital STL/OBJ/FBX files,
          ZBrush-based modeling, and related 3D printing design services.
        </p>

        <ul>
          <li>
            <strong>Website:</strong> 3dspot.io
          </li>
          <li>
            <strong>Business name:</strong> 3D Spot
          </li>
          <li>
            <strong>Contact email:</strong>{" "}
            <a href="mailto:project@3dspot.io">project@3dspot.io</a>
          </li>
          <li>
            <strong>Country:</strong> India
          </li>
        </ul>

        <p>
          For data protection purposes, 3D Spot is the data controller for
          personal information collected directly through our website, forms,
          emails, messages, and customer communications.
        </p>

        <p>
          For Facebook Page Insights and certain Meta tools, Meta may also act
          as an independent or joint controller according to Meta’s applicable
          terms.
        </p>

        <h2>2. Information We Collect</h2>

        <p>We may collect the following types of information:</p>

        <h3>Personal information you provide to us</h3>

        <p>This may include:</p>

        <ul>
          <li>Name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Country, state, city, or billing details</li>
          <li>Business name, if applicable</li>
          <li>Social media profile name or username</li>
          <li>Messages, inquiries, and quotation requests</li>
          <li>
            Reference images, photos, sketches, screenshots, videos, or files you
            send to us
          </li>
          <li>Project instructions and design preferences</li>
          <li>
            Order details, payment status, and transaction-related information
          </li>
          <li>Feedback, revisions, approvals, and support communications</li>
        </ul>

        <h3>Project and creative files</h3>

        <p>
          Because our service involves custom 3D sculpting, you may send us
          reference photos, character images, product references, artwork, or
          other visual material. We use these files only to understand your
          project and create the requested 3D model or related service.
        </p>

        <p>
          You must have the legal right or permission to send us any image,
          photo, likeness, brand, character, copyrighted artwork, or third-party
          material.
        </p>

        <h3>Payment information</h3>

        <p>
          Payments may be processed by third-party payment providers such as
          Stripe, PayPal, bank transfer providers, or other payment gateways. We
          do not store full card numbers or sensitive payment authentication data
          on our own systems.
        </p>

        <h3>Website and usage data</h3>

        <p>When you visit our website, we may automatically collect:</p>

        <ul>
          <li>IP address</li>
          <li>Browser type and version</li>
          <li>Device type</li>
          <li>Operating system</li>
          <li>Pages visited</li>
          <li>Time spent on pages</li>
          <li>Referring website or ad source</li>
          <li>Approximate location based on IP address</li>
          <li>
            Cookies, pixels, tags, and similar tracking identifiers, where
            permitted
          </li>
        </ul>

        <h2>3. Cookies, Tracking Technologies, Meta Pixel and Similar Tools</h2>

        <p>
          We may use cookies, pixels, tags, server-side tracking, analytics
          tools, and advertising technologies to operate our website, improve
          performance, measure ads, prevent fraud, and show relevant
          advertising.
        </p>

        <p>These may include:</p>

        <ul>
          <li>Essential cookies required for the website to work</li>
          <li>Analytics cookies</li>
          <li>Advertising and remarketing cookies</li>
          <li>Meta Pixel</li>
          <li>Meta Conversion API</li>
          <li>Google Analytics</li>
          <li>Google Ads tags</li>
          <li>Other similar advertising or analytics tools</li>
        </ul>

        <p>
          Where required by law, including in the European Economic Area, United
          Kingdom, and similar jurisdictions, we will use non-essential cookies
          and advertising tracking technologies only after receiving your
          consent.
        </p>

        <p>
          You may withdraw or change your cookie consent at any time through our
          cookie settings tool, browser settings, or by contacting us.
        </p>

        <p>
          Essential cookies may still be used without consent where they are
          strictly necessary to provide the website or service you requested.
        </p>

        <h2>4. How We Use Your Information</h2>

        <p>We use personal information for the following purposes:</p>

        <ul>
          <li>To respond to your inquiries</li>
          <li>To provide quotations</li>
          <li>
            To create custom STL, OBJ, FBX, ZBrush, or 3D printing-ready files
          </li>
          <li>To process orders and payments</li>
          <li>To provide revisions and customer support</li>
          <li>To deliver digital files after payment and approval</li>
          <li>To communicate about project progress</li>
          <li>To send invoices, receipts, and order updates</li>
          <li>To prevent fraud, misuse, or unauthorized access</li>
          <li>To improve our website, ads, and services</li>
          <li>To measure the performance of our advertising campaigns</li>
          <li>
            To run remarketing or custom audience campaigns where legally
            permitted
          </li>
          <li>To comply with legal, tax, accounting, and business obligations</li>
          <li>To protect our rights, property, customers, and business</li>
        </ul>

        <h2>5. Legal Basis for Processing Under GDPR</h2>

        <p>
          If you are located in the European Economic Area, United Kingdom, or
          another region with similar data protection laws, we process your
          personal information under one or more of the following legal bases:
        </p>

        <h3>Performance of a contract</h3>

        <p>
          We process your data when it is necessary to provide a quotation,
          create a 3D model, deliver files, manage revisions, process payment, or
          complete your order.
        </p>

        <h3>Consent</h3>

        <p>
          We may rely on your consent for non-essential cookies, marketing
          emails, remarketing, use of certain tracking technologies, and any
          optional processing where consent is legally required.
        </p>

        <h3>Legitimate interests</h3>

        <p>
          We may process information for legitimate business purposes such as
          customer support, fraud prevention, service improvement, business
          communication, security, and measuring basic business performance,
          provided your privacy rights do not override those interests.
        </p>

        <h3>Legal obligation</h3>

        <p>
          We may process information where required for tax, accounting, legal
          compliance, dispute resolution, or government requests.
        </p>

        <h2>6. Facebook, Instagram, Meta Ads and Lead Forms</h2>

        <p>
          We operate Facebook and Instagram pages and may run Meta advertising
          campaigns.
        </p>

        <p>
          When you interact with our Facebook Page, Instagram profile, ads,
          Messenger, comments, or Lead Forms, Meta may collect and process
          information according to its own privacy policies and terms.
        </p>

        <p>We may receive information from Meta such as:</p>

        <ul>
          <li>Name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Form answers</li>
          <li>Message content</li>
          <li>Lead form submissions</li>
          <li>Engagement with our ads or page</li>
          <li>Page Insights and ad performance data</li>
        </ul>

        <p>
          We use this information to respond to inquiries, provide quotations,
          contact potential customers, measure advertising performance, and
          improve our services.
        </p>

        <p>
          For Meta Lead Forms, we use the information you submit only for the
          purpose described in the form, such as contacting you about a custom
          STL, 3D sculpting quote, or related service.
        </p>

        <p>We do not sell lead form data.</p>

        <h2>7. Custom Audiences, Remarketing and Advertising</h2>

<p>
  Where permitted by law, we may use customer information, website
  activity, or engagement data to create advertising audiences, custom
  audiences, lookalike audiences, remarketing audiences, or conversion
  tracking reports on platforms such as Meta, Google, and similar ad
  platforms.
</p>

<p>
  In regions where consent is required, we will use advertising cookies,
  remarketing tags, and similar tracking technologies only after receiving
  valid consent.
</p>

<p>
  You can control ad preferences through your Facebook, Instagram, Google,
  browser, or device settings.
</p>

        <h2>8. CalOPPA Notice for California Users</h2>

        <p>
          In accordance with the California Online Privacy Protection Act, we
          make this Privacy Policy available through a clear link on our website.
        </p>

        <p>This Privacy Policy explains:</p>

        <ul>
          <li>The categories of personal information we collect</li>
          <li>How we use personal information</li>
          <li>The types of third parties with whom we may share information</li>
          <li>
            How users may contact us to review, update, or delete their
            information
          </li>
          <li>How we notify users of material changes to this Privacy Policy</li>
          <li>The effective date of this Privacy Policy</li>
        </ul>

        <h3>Do Not Track Signals</h3>

        <p>
          Some browsers offer a “Do Not Track” signal. Because there is no
          consistent industry standard for responding to Do Not Track signals,
          our website may not respond to all such signals.
        </p>

        <p>
          However, you can control cookies through your browser settings, cookie
          consent tool, and advertising platform settings.
        </p>

        <h3>Third-party tracking</h3>

        <p>
          Third parties such as Meta, Google, analytics providers, payment
          processors, or advertising partners may collect information about your
          activity over time and across different websites when cookies or
          tracking technologies are enabled.
        </p>

        <h2>9. Children’s Privacy</h2>

        <p>
          Our website and services are not directed to children under the age of
          13.
        </p>

        <p>
          We do not knowingly collect personal information directly from children
          under 13.
        </p>

        <p>
          If you are under 13, you should not use our website or submit personal
          information to us.
        </p>

        <p>
          If a parent or legal guardian sends us a child’s photo, reference
          image, or likeness for a custom 3D sculpting project, the parent or
          guardian confirms that they have the legal authority and consent to
          provide that material.
        </p>

        <p>
          If we become aware that we have collected personal information from a
          child under 13 without verified parental consent, we will take
          reasonable steps to delete that information.
        </p>

        <p>
          For users in the European Economic Area or United Kingdom, we may apply
          a higher age threshold where required by local law.
        </p>

        <h2>10. Sharing of Information</h2>

        <p>We may share personal information with:</p>

        <ul>
          <li>Payment processors</li>
          <li>Hosting providers</li>
          <li>Cloud storage providers</li>
          <li>Email and communication providers</li>
          <li>Analytics and advertising platforms</li>
          <li>Freelancers, artists, or employees working on your project</li>
          <li>Legal, accounting, tax, or compliance advisors</li>
          <li>Government authorities if required by law</li>
          <li>Business partners only where necessary for the requested service</li>
        </ul>

        <p>We do not sell your personal information.</p>

        <p>
          We require service providers to use personal information only for the
          services they provide to us and to protect it appropriately.
        </p>

        <h2>11. International Data Transfers</h2>

        <p>
          Because we operate from India and may use global service providers,
          your personal information may be transferred to and processed in
          countries outside your country of residence, including India, the
          United States, and other locations where our service providers operate.
        </p>

        <p>
          Where required by applicable law, we use appropriate safeguards for
          international data transfers, such as contractual protections, security
          measures, and service providers with recognized privacy commitments.
        </p>

        <h2>12. Data Retention</h2>

        <p>
          We keep personal information only for as long as reasonably necessary
          for the purposes described in this Privacy Policy.
        </p>

        <p>Typical retention periods may include:</p>

        <ul>
          <li>Inquiry and quotation data: up to 24 months</li>
          <li>
            Customer project files and communications: up to 36 months after
            project completion
          </li>
          <li>
            Payment, invoice, tax, and accounting records: as required by
            applicable law
          </li>
          <li>
            Website analytics and advertising data: according to the relevant
            platform retention settings
          </li>
          <li>
            Legal or dispute-related records: as long as necessary to protect
            our rights
          </li>
        </ul>

        <p>
          You may request deletion of your personal information, subject to
          legal, contractual, tax, accounting, or dispute-related retention
          requirements.
        </p>

        <h2>13. Security</h2>

        <p>
          We use reasonable technical, organizational, and administrative
          measures to protect personal information from unauthorized access,
          loss, misuse, alteration, or disclosure.
        </p>

        <p>
          However, no method of online transmission or electronic storage is
          completely secure. We cannot guarantee absolute security.
        </p>

        <h2>14. Your Privacy Rights</h2>

        <p>Depending on your location, you may have the right to:</p>

        <ul>
          <li>Access the personal information we hold about you</li>
          <li>Correct inaccurate or incomplete information</li>
          <li>Request deletion of your information</li>
          <li>Object to certain processing</li>
          <li>Restrict processing</li>
          <li>Withdraw consent</li>
          <li>Request data portability</li>
          <li>Opt out of marketing communications</li>
          <li>Lodge a complaint with a data protection authority</li>
        </ul>

        <p>
          To exercise your rights, contact us using the contact details in this
          Privacy Policy.
        </p>

        <p>
          We may need to verify your identity before processing your request.
        </p>

        <h2>15. Marketing Communications</h2>

        <p>
          We may send you service-related messages about your inquiry, quotation,
          order, payment, revisions, or file delivery.
        </p>

        <p>
          We may send marketing messages only where permitted by law or where you
          have agreed to receive them.
        </p>

        <p>
          You can opt out of marketing communications at any time by contacting
          us or using the unsubscribe option where available.
        </p>

        <h2>16. User-Submitted Images, Copyright and Likeness Rights</h2>

        <p>
          When you submit images, artwork, characters, logos, celebrity
          references, product designs, or personal likenesses, you confirm that
          you have the right, permission, license, or authority to use and
          provide those materials to us.
        </p>

        <p>
          We are not responsible for disputes arising from materials submitted by
          customers without proper rights or permission.
        </p>

        <p>
          We may refuse projects that appear to violate intellectual property
          rights, privacy rights, publicity rights, or applicable law.
        </p>

        <h2>17. Public Portfolio and Marketing Use</h2>

        <p>
          We will not publicly post your private reference photos, personal
          images, or final project renders as portfolio or marketing content
          without your permission, unless the work is already public or you have
          separately approved promotional use.
        </p>

        <p>
          If we ask for permission to use your project in our portfolio, you may
          refuse.
        </p>

        <h2>18. Links to Other Websites</h2>

        <p>
          Our website, ads, emails, or messages may include links to third-party
          websites, platforms, or payment providers.
        </p>

        <p>
          We are not responsible for the privacy practices or content of
          third-party websites. Please review their privacy policies before
          submitting information.
        </p>

        <h2>19. Changes to This Privacy Policy</h2>

        <p>We may update this Privacy Policy from time to time.</p>

        <p>
          When we make changes, we will update the “Last updated” date at the top
          of this page.
        </p>

        <p>
          Material changes may be notified through our website, email, or other
          reasonable method.
        </p>

        <h2>20. Contact Us</h2>

        <p>For questions, requests, or privacy concerns, contact us at:</p>

        <ul>
          <li>
            <strong>3D Spot</strong>
          </li>
          <li>
            <strong>Website:</strong> 3dspot.io
          </li>
          <li>
            <strong>Email:</strong>{" "}
            <a href="mailto:project@3dspot.io">project@3dspot.io</a>
          </li>
          <li>
            <strong>Country:</strong> India
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;